import { useQuery } from '@tanstack/react-query';
import { LuCheck, LuCircleAlert, LuCircleCheck, LuCircleMinus, LuCornerDownRight } from 'react-icons/lu';

import { hardnessRuleQueryOptions } from '~api/queries';
import type { BlockFeature, HardnessRuleQueryParams, HardnessRulesetItem, HardnessTraceStep } from '~api/services';
import { ErrorMessage, Skeleton } from '~components';
import { getHardnessRuleState, getHardnessRuleStepByName } from '../utils/hardness-rule-trace';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type BlockHardnessRuleTraceProps = {
  /** The block whose hardness calculation trace should be displayed */
  block: BlockFeature;
  /** Whether the hardness rule trace query should run */
  enabled?: boolean;
};

type HardnessRuleProps = {
  /** Static rule definition from the backend ruleset */
  rule: HardnessRulesetItem;
  /** Optional evaluated trace step matching the rule name */
  step?: HardnessTraceStep;
};

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/** Displays a single rule and the matching evaluation result, if one exists. */
const HardnessRule = ({ rule, step }: HardnessRuleProps) => {
  const stepRan = step != null;
  const state = getHardnessRuleState(step);

  return (
    <div className="flex items-start gap-1.5 p-0">
      {state === 'error' ? (
        <LuCircleAlert className="mt-[3px] shrink-0 text-destructive" aria-label="Error" />
      ) : state === 'not-evaluated' ? (
        <LuCircleMinus className="mt-[3px] shrink-0 text-muted-foreground" aria-label="Not evaluated" />
      ) : state === 'skipped' ? (
        <LuCircleCheck className="mt-[3px] shrink-0 text-info" aria-label="Skipped" />
      ) : state === 'completed' ? (
        <LuCheck className="mt-[3px] shrink-0 text-success" aria-label="Completed" />
      ) : (
        <LuCircleCheck className="mt-[3px] shrink-0 text-info" aria-label="Evaluated" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-col text-muted-foreground text-sm">
          <div className={stepRan ? 'font-bold text-foreground' : 'font-bold text-muted-foreground'}>
            {rule.description}
          </div>
          {stepRan && step.result.hardness && (
            <span className="mt-0.5 text-xs">
              <LuCornerDownRight className="mr-[1px] mb-[1px] inline pb-0.5" aria-hidden="true" />
              Hardness: {step.result.hardness}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Returns the mineralisation value to use for the hardness rule query.
 *
 * The raw block 'mineralisation' value is returned as a number by older API versions.
 * Later versions return the human meaningful mineralisation string, e.g. 'waste', and
 * a mineralisation number as mineralisation_value.
 *
 * Provides backwards capability and ensures a number for the hardness rule query.
 **/
const getMineralisationRuleValue = (block: BlockFeature): number | undefined => {
  const { mineralisation, mineralisation_value } = block.properties;

  if (typeof mineralisation === 'number') return mineralisation;

  return mineralisation_value;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Displays the backend hardness rule trace for a selected block.
 *
 * The ruleset is used as the display source so all configured rules are shown,
 * even when an individual trace step was skipped or not returned by the trace.
 */
const BlockHardnessRuleTrace = ({ block, enabled = true }: BlockHardnessRuleTraceProps) => {
  const params: HardnessRuleQueryParams = {
    strand: block.properties.strand,
    geology: block.properties.geology,
    mineralisation: getMineralisationRuleValue(block),
    depth: block.properties.depth,
  };

  const { data, isLoading, isError } = useQuery({
    ...hardnessRuleQueryOptions(params),
    enabled,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h3 className="font-bold text-lg">Hardness Rule</h3>
        <Skeleton className="h-16 w-full" aria-label="Loading hardness rule" />
      </div>
    );
  }
  if (isError) {
    return (
      <ErrorMessage
        intent="warning"
        title="Hardness rule unavailable"
        message="The hardness rule trace could not be loaded for this block."
      />
    );
  }
  if (!data) {
    return null;
  }

  const stepByName = getHardnessRuleStepByName(data.trace.steps);

  return (
    <section className="space-y-4 rounded-[0.75rem] bg-muted p-3">
      <h3 className="mb-3 font-bold text-lg">Hardness Calculation Steps</h3>
      <div className="space-y-3">
        {data.ruleset.map((rule) => {
          const step = stepByName.get(rule.name);
          return <HardnessRule key={rule.name} rule={rule} step={step} />;
        })}
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlockHardnessRuleTrace };
